import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { AccionModel } from '../sso/models/menu.accion.model';
import {
  MenuAppEmpModel,
  MenuAppModel,
  MenuModel,
  MenuOptionsModel,
} from '../sso/models/menu.model';
import {
  ModuloAppEmpModel,
  ModuloModel,
} from 'src/app/services/sso/models/modulo.model';
import { ApartadoModel } from '../sso/models/apartado.model';
import { PermisoModel } from '../sso/models/rol.menu.opciones.model';
import { firstValueFrom } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class MenuService {
  constructor(private http: HttpClient) {}

  // Obtener menus
  async getMenus(): Promise<MenuModel[]> {
    let result: MenuModel[];
    try {
      result = await this.http
        .get<MenuModel[]>(environment.apiSSO + '/sso/menus')
        .toPromise();
    } catch (e) {
      console.log(e);
    }
    return result;
  }

  // Obtener un menú
  async getMenu(id: number): Promise<MenuModel> {
    let result: MenuModel;
    try {
      result = await this.http
        .get<MenuModel>(environment.apiSSO + '/sso/menu/' + id)
        .toPromise();
    } catch (e) {
      console.log(e);
    }
    return result;
  }

  // Obtener un menú con todas las opciones de los posibles módulos
  async getMenuCompleto(id: number): Promise<MenuModel> {
    let result: MenuModel;
    try {
      result = await this.http
        .get<MenuModel>(environment.apiSSO + '/sso/menu/completo/' + id)
        .toPromise();
      if (result && result.Opciones) {
        result.Opciones.forEach((opc) => {
          if (opc.Observaciones && opc.Observaciones.length > 0) {
            opc.TextoId =
              opc.TextoId + '<br><sup>' + opc.Observaciones + '</sup>';
          }
        });
      }
    } catch (e) {
      console.log(e);
    }
    return result;
  }

  // Obtener modulos
  async getModulos(): Promise<ModuloModel[]> {
    let result: ModuloModel[];
    try {
      result = await this.http
        .get<ModuloModel[]>(environment.apiSSO + '/sso/modulos')
        .toPromise();
    } catch (e) {
      console.log(e);
    }
    return result;
  }

  //Crear/modificar modulos
  async saveModulo(modulo: ModuloModel): Promise<any> {
    let result: ModuloModel;
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
    };
    try {
      result = await this.http
        .post<ModuloModel>(
          environment.apiSSO + '/sso/modulos/crear',
          JSON.stringify(modulo),
          httpOptions,
        )
        .toPromise();
    } catch (e) {
      console.log(e);
    }
    return result;
  }

  // Obtener acciones
  async getActions(): Promise<AccionModel[]> {
    let result: AccionModel[];
    try {
      result = await this.http
        .get<AccionModel[]>(environment.apiSSO + '/sso/menu/acciones')
        .toPromise();
    } catch (e) {
      console.log(e);
    }
    return result;
  }

  // Crear/modificar acciones
  async addEditActions(Action): Promise<any> {
    let result;
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
    };
    try {
      result = await this.http
        .post<any>(
          environment.apiSSO + '/sso/menu/accion/crear',
          JSON.stringify(Action),
          httpOptions,
        )
        .toPromise();
    } catch (e) {
      console.log(e);
    }
    return result;
  }

  // Crea o modifica un menú
  async saveMenu(menu: MenuModel): Promise<any> {
    let result;
    MenuModel;
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
    };
    try {
      result = await this.http
        .post<MenuModel>(
          environment.apiSSO + '/sso/menu/crear',
          JSON.stringify(menu),
          httpOptions,
        )
        .toPromise();
    } catch (e) {
      console.log(e);
    }
    return result;
  }

  async saveMenuV2(menu: MenuModel): Promise<any> {
    let result;
    MenuModel;
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
    };
    return firstValueFrom(
      this.http.post<MenuModel>(
        environment.apiSSO + '/sso/menu/crear',
        JSON.stringify(menu),
        httpOptions,
      ),
    );
  }

  // Borrar menú
  async deleteMenu(id: number): Promise<void> {
    try {
      return await this.http
        .delete<void>(environment.apiSSO + '/sso/menu/borrar/' + id)
        .toPromise();
    } catch (e) {
      console.log(e);
    }
  }

  //Borrar modulo
  async deleteModulo(id: number): Promise<void> {
    try {
      return await this.http
        .delete<void>(environment.apiSSO + '/sso/modulos/borrar/' + id)
        .toPromise();
    } catch (e) {
      console.log(e);
    }
  }

  // Obtener menus de aplicaciones
  async getMenusApp(): Promise<MenuAppModel[]> {
    let result: MenuAppModel[];
    try {
      result = await this.http
        .get<MenuAppModel[]>(environment.apiSSO + '/sso/menus/aplicaciones')
        .toPromise();
    } catch (e) {
      console.log(e);
    }
    return result ? result : [];
  }

  // Obtener el menú de una aplicación
  async getMenuApp(appId: number): Promise<MenuAppModel> {
    let result: MenuAppModel;
    try {
      result = await this.http
        .get<MenuAppModel>(environment.apiSSO + '/sso/menu/aplicacion/' + appId)
        .toPromise();
    } catch (e) {
      console.log(e);
    }
    return result;
  }

  // Obtener el menú de una aplicación con todas las opciones de los módulos que se hayan definido para esta app
  async getMenuAppCompleto(appId: number): Promise<MenuModel> {
    let result: MenuModel;
    try {
      result = await this.http
        .get<MenuModel>(
          environment.apiSSO + '/sso/menu/aplicacioncompleto/' + appId,
        )
        .toPromise();
      if (result && result.Opciones) {
        result.Opciones.forEach((opc) => {
          if (opc.Observaciones && opc.Observaciones.length > 0) {
            opc.TextoId =
              opc.TextoId + '<br><sup>' + opc.Observaciones + '</sup>';
          }
        });
      }
    } catch (e) {
      console.log(e);
    }
    return result;
  }

  // Asigna un menú a una aplicación
  async saveMenuAplicacion(menu: MenuAppModel): Promise<any> {
    let result;
    MenuAppModel;
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
    };
    try {
      result = await this.http
        .post<MenuAppModel>(
          environment.apiSSO + '/sso/menu/aplicacion/asignar',
          JSON.stringify(menu),
          httpOptions,
        )
        .toPromise();
    } catch (e) {
      console.log(e);
    }
    return result;
  }

  // Obtener menus de aplicaciones por empresa
  async getMenusAppEmp(): Promise<MenuAppEmpModel[]> {
    let result: MenuAppEmpModel[];
    try {
      result = await this.http
        .get<
          MenuAppEmpModel[]
        >(environment.apiSSO + '/sso/menus/aplicaciones/empresas')
        .toPromise();
    } catch (e) {
      console.log(e);
    }
    return result;
  }

  // Asigna un menú a una aplicación y empresa
  async saveMenuAplicacionEmpresa(menu: MenuAppEmpModel): Promise<any> {
    let result;
    MenuAppEmpModel;
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
    };
    try {
      result = await this.http
        .post<MenuAppEmpModel>(
          environment.apiSSO + '/sso/menu/aplicacion/empresa/asignar',
          JSON.stringify(menu),
          httpOptions,
        )
        .toPromise();
    } catch (e) {
      console.log(e);
    }
    return result;
  }

  // Borrar menú de una aplicacion para una empresa
  async deleteMenuAppEmpresa(id: number): Promise<void> {
    try {
      return await this.http
        .delete<void>(
          environment.apiSSO + '/sso/menu/aplicacion/empresa/borrar/' + id,
        )
        .toPromise();
    } catch (e) {
      console.log(e);
    }
  }

  // Borrar modulo por aplicacion empresa
  async deleteModuloAppEmpresa(reg: ModuloAppEmpModel): Promise<any> {
    let result: ModuloAppEmpModel;
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
    };
    try {
      result = await this.http
        .post<ModuloAppEmpModel>(
          environment.apiSSO + '/sso/modulos/aplicacion/borrar',
          JSON.stringify(reg),
          httpOptions,
        )
        .toPromise();
    } catch (e) {
      console.log(e);
    }
    return result;
  }

  // Crear modulo por aplicacion empresa
  async saveModulosAplicacionEmpresa(reg: ModuloAppEmpModel): Promise<any> {
    let result: ModuloAppEmpModel;
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
    };
    try {
      result = await this.http
        .post<ModuloAppEmpModel>(
          environment.apiSSO + '/sso/modulos/aplicacion/crear',
          JSON.stringify(reg),
          httpOptions,
        )
        .toPromise();
    } catch (e) {
      console.log(e);
    }
    return result;
  }

  // Obtener modulos por aplicacion
  async getModulosByApp(): Promise<ModuloAppEmpModel[]> {
    let result: ModuloAppEmpModel[];
    try {
      result = await this.http
        .get<ModuloAppEmpModel[]>(environment.apiSSO + '/sso/modulos/appemp')
        .toPromise();
    } catch (e) {
      console.log(e);
    }
    return result;
  }

  // Obtener los apartados de una opción del menú
  async getApartados(id: number): Promise<ApartadoModel[]> {
    let result: ApartadoModel[];
    try {
      result = await this.http
        .get<ApartadoModel[]>(environment.apiSSO + '/sso/apartados/' + id)
        .toPromise();
    } catch (e) {
      console.log(e);
    }
    return result;
  }

  // Crear modulo por aplicacion empresa
  async saveApartado(reg: ApartadoModel): Promise<ApartadoModel> {
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
    };
    try {
      return await this.http
        .post<ApartadoModel>(
          environment.apiSSO + '/sso/apartado',
          JSON.stringify(reg),
          httpOptions,
        )
        .toPromise();
    } catch (e) {
      console.log(e);
    }
    return null;
  }

  async deleteApartado(id: number): Promise<boolean> {
    try {
      return await this.http
        .delete<boolean>(environment.apiSSO + '/sso/apartado/' + id)
        .toPromise();
    } catch (e) {
      console.log(e);
    }
    return false;
  }

  // Obtener los permisos de los apartados de una aplicación
  async getPermisosAppApartados(
    idApp: number,
    idOpcionMenu: number,
    idApartado: number,
  ): Promise<PermisoModel[]> {
    let result: PermisoModel[];
    try {
      result = await this.http
        .get<
          PermisoModel[]
        >(environment.apiSSO + '/sso/apartados/permisos/' + idApp + '/' + idOpcionMenu + '/' + idApartado)
        .toPromise();
    } catch (e) {
      console.log(e);
    }
    return result;
  }
}
